import React from 'react';
import { motion } from 'framer-motion';

const ParticlesBackground: React.FC = () => {
  // Generate particles with different properties for variety
  const generateParticles = (count: number) => {
    return Array.from({ length: count }, (_, index) => {
      const size = Math.random() * 3 + 2;
      const initialX = Math.random() * 100;
      const initialY = Math.random() * 100;
      const opacity = Math.random() * 0.4 + 0.2;
      const duration = Math.random() * 8 + 10;
      const delay = Math.random() * 5;
      
      return {
        id: index,
        size,
        initialX,
        initialY,
        opacity,
        duration,
        delay,
      };
    });
  };

  // Create different particle groups for different sections
  const topParticles = generateParticles(30);
  const middleParticles = generateParticles(25);
  const bottomParticles = generateParticles(35);
  const leftSideParticles = generateParticles(20);

  // Generate star clusters
  const starClusters = Array.from({ length: 5 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 150 + 100,
    particleCount: Math.floor(Math.random() * 20 + 15),
  }));

  return (
    <div className="fixed inset-0 pointer-events-none z-5 overflow-hidden">
      {/* Star clusters */}
      {starClusters.map((cluster) => (
        <div
          key={`cluster-${cluster.id}`}
          className="absolute"
          style={{
            left: `${cluster.x}%`,
            top: `${cluster.y}%`,
            width: cluster.size,
            height: cluster.size,
          }}
        >
          {Array.from({ length: cluster.particleCount }).map((_, i) => {
            const angle = (Math.PI * 2 * i) / cluster.particleCount;
            const radius = Math.random() * (cluster.size / 2);
            const x = Math.cos(angle) * radius + cluster.size / 2;
            const y = Math.sin(angle) * radius + cluster.size / 2;
            
            return (
              <motion.div
                key={`cluster-star-${i}`}
                className="absolute rounded-full bg-accent/60"
                style={{
                  width: Math.random() * 2 + 1,
                  height: Math.random() * 2 + 1,
                  left: x,
                  top: y,
                  boxShadow: '0 0 3px rgba(73, 17, 28, 0.8)',
                }}
                animate={{
                  opacity: [0.3, 0.7, 0.3],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: Math.random() * 3 + 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            );
          })}
        </div>
      ))}

      {/* Top section particles */}
      {topParticles.map((particle) => (
        <motion.div
          key={`top-${particle.id}`}
          className="absolute rounded-full bg-accent/60"
          style={{
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            left: `${particle.initialX}%`,
            top: `${particle.initialY * 0.3}%`, // Keep in top 30%
            opacity: particle.opacity,
            boxShadow: `0 0 ${particle.size * 2}px rgba(73, 17, 28, 0.8)`,
          }}
          animate={{
            x: [0, Math.random() * 40 - 20, Math.random() * -40 + 20, 0],
            y: [0, Math.random() * 40 - 20, Math.random() * -40 + 20, 0],
            opacity: [particle.opacity, particle.opacity + 0.2, particle.opacity - 0.1, particle.opacity],
            scale: [1, 1.2, 0.8, 1],
          }}
          transition={{
            duration: particle.duration * 1.5,
            delay: particle.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Middle section particles */}
      {middleParticles.map((particle) => (
        <motion.div
          key={`middle-${particle.id}`}
          className="absolute rounded-full bg-accent/60"
          style={{
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            left: `${particle.initialX}%`,
            top: `${30 + particle.initialY * 0.4}%`, // Between 30% and 70%
            opacity: particle.opacity,
            boxShadow: `0 0 ${particle.size * 2}px rgba(73, 17, 28, 0.8)`,
          }}
          animate={{
            x: [0, Math.random() * 40 - 20, Math.random() * -40 + 20, 0],
            y: [0, Math.random() * 40 - 20, Math.random() * -40 + 20, 0],
            opacity: [particle.opacity, particle.opacity + 0.2, particle.opacity - 0.1, particle.opacity],
          }}
          transition={{
            duration: particle.duration * 1.5,
            delay: particle.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Bottom section particles */}
      {bottomParticles.map((particle) => (
        <motion.div
          key={`bottom-${particle.id}`}
          className="absolute rounded-full bg-accent/60"
          style={{
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            left: `${particle.initialX}%`,
            top: `${70 + particle.initialY * 0.3}%`, // Between 70% and 100%
            opacity: particle.opacity,
            boxShadow: `0 0 ${particle.size * 2}px rgba(73, 17, 28, 0.8)`,
          }}
          animate={{
            x: [0, Math.random() * 35 - 17.5, Math.random() * -35 + 17.5, 0],
            y: [0, Math.random() * 35 - 17.5, Math.random() * -35 + 17.5, 0],
            opacity: [particle.opacity, particle.opacity + 0.2, particle.opacity - 0.1, particle.opacity],
          }}
          transition={{
            duration: particle.duration * 1.5,
            delay: particle.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Left side particles */}
      {leftSideParticles.map((particle) => (
        <motion.div
          key={`left-${particle.id}`}
          className="absolute rounded-full bg-accent/60"
          style={{
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            left: `${particle.initialX * 0.3}%`, // Between 0% and 30%
            top: `${particle.initialY}%`,
            opacity: particle.opacity,
            boxShadow: `0 0 ${particle.size * 2}px rgba(73, 17, 28, 0.8)`,
          }}
          animate={{
            x: [0, Math.random() * 25 - 12.5, Math.random() * -25 + 12.5, 0],
            y: [0, Math.random() * 50 - 25, Math.random() * -50 + 25, 0],
            opacity: [particle.opacity, particle.opacity + 0.2, particle.opacity - 0.1, particle.opacity],
          }}
          transition={{
            duration: particle.duration * 1.5,
            delay: particle.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Additional floating particles that move across sections */}
      {Array.from({ length: 15 }).map((_, index) => {
        const size = Math.random() * 4 + 3;
        const startX = Math.random() * 100;
        const startY = Math.random() * 100;
        const duration = Math.random() * 20 + 30;
        const delay = Math.random() * 10;
        
        return (
          <motion.div
            key={`floating-${index}`}
            className="absolute rounded-full bg-accent/70"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              left: `${startX}%`,
              top: `${startY}%`,
              opacity: 0.3,
              boxShadow: `0 0 ${size * 3}px rgba(73, 17, 28, 0.9)`,
              zIndex: 6,
            }}
            animate={{
              x: [0, Math.random() * 100 - 50, Math.random() * -100 + 50, 0],
              y: [0, Math.random() * 100 - 50, Math.random() * -100 + 50, 0],
              opacity: [0.3, 0.6, 0.4, 0.3],
              scale: [1, 1.2, 0.9, 1],
            }}
            transition={{
              duration: duration * 1.5,
              delay: delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        );
      })}
    </div>
  );
};

export default ParticlesBackground;